import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import Sidebar from '../components/Sidebar';

export default function PastaCliente() {
  const { clienteId } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [anexos, setAnexos] = useState([]);
  const [temas, setTemas] = useState([]);
  const [temaSelecionado, setTemaSelecionado] = useState('');
  const [uploading, setUploading] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => { init(); }, [clienteId]);

  async function init() {
    const [clienteRes, anexosRes, temasRes] = await Promise.all([
      api.get(`/clientes/${clienteId}`),
      api.get(`/anexos/pasta/${clienteId}`),
      api.get('/parametros/tema_documento').catch(() => ({ data: [] })),
    ]);
    setCliente(clienteRes.data);
    setAnexos(anexosRes.data);
    setTemas(temasRes.data.length > 0
      ? temasRes.data.map(t => t.valor).filter(t => t !== 'Outros')
      : ['Contrato', 'Procuração', 'Documento Pessoal', 'Ato Societário', 'Comprovante', 'NF-e', 'Boleto']
    );
  }

  async function upload(e) {
    const arquivo = e.target.files[0];
    if (!arquivo) return;
    if (!temaSelecionado) {
      alert('Selecione o tipo de documento antes de anexar!');
      e.target.value = '';
      return;
    }
    setUploading(true);
    setErro('');
    try {
      const formData = new FormData();
      formData.append('arquivo', arquivo);
      formData.append('cliente_id', clienteId);
      formData.append('tema', temaSelecionado);
      await api.post('/anexos/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const res = await api.get(`/anexos/pasta/${clienteId}`);
      setAnexos(res.data);
    } catch {
      setErro('Erro ao fazer upload. Tente novamente.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function download(anexo) {
    try {
      const res = await api.get(`/anexos/${anexo.id}/download`);
      window.open(res.data.url, '_blank');
    } catch {
      alert('Erro ao baixar arquivo.');
    }
  }

  async function deletar(id) {
    if (!window.confirm('Remover este anexo?')) return;
    await api.delete(`/anexos/${id}`);
    const res = await api.get(`/anexos/pasta/${clienteId}`);
    setAnexos(res.data);
  }

  function formatarData(dt) {
    if (!dt) return '—';
    return new Date(dt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  function iconeArquivo(nome) {
    const ext = nome.split('.').pop().toLowerCase();
    if (['pdf'].includes(ext)) return '📄';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return '🖼️';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['xls', 'xlsx'].includes(ext)) return '📊';
    return '📎';
  }

  const porTema = anexos.reduce((acc, a) => {
    const t = a.tema || 'Sem categoria';
    if (!acc[t]) acc[t] = [];
    acc[t].push(a);
    return acc;
  }, {});

  return (
    <div style={styles.container}>
      <Sidebar />
      <div style={styles.conteudo}>
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button style={styles.botaoVoltar} onClick={() => navigate('/clientes')}>← Voltar</button>
            <span style={{ fontSize: '22px' }}>📁</span>
            <div>
              <h1 style={styles.titulo}>Pasta do Cliente</h1>
              {cliente && <p style={styles.subtitulo}>{cliente.razao_social}</p>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select style={styles.selectTema} value={temaSelecionado} onChange={e => setTemaSelecionado(e.target.value)}>
              <option value="">Selecione o tipo...</option>
              {temas.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <label style={styles.botaoUpload}>
              {uploading ? 'Enviando...' : '+ Anexar arquivo'}
              <input type="file" style={{ display: 'none' }} onChange={upload} disabled={uploading} />
            </label>
          </div>
        </div>

        {erro && <p style={styles.erro}>{erro}</p>}

        {Object.keys(porTema).length === 0 ? (
          <div style={styles.vazio}>
            <p style={{ fontSize: '48px', marginBottom: '1rem' }}>📂</p>
            <p style={{ color: '#aaa', fontSize: '14px' }}>Nenhum documento anexado</p>
          </div>
        ) : (
          Object.keys(porTema).sort().map(tema => (
            <div key={tema} style={styles.grupoTema}>
              <div style={styles.temaHeader}>
                <span style={styles.temaTitulo}>📂 {tema}</span>
                <span style={styles.temaCount}>{porTema[tema].length} arquivo{porTema[tema].length > 1 ? 's' : ''}</span>
              </div>
              <div style={styles.lista}>
                {porTema[tema].map(a => (
                  <div key={a.id} style={styles.item}>
                    <span style={styles.icone}>{iconeArquivo(a.nome_arquivo)}</span>
                    <div style={styles.info}>
                      <span style={styles.nome}>{a.nome_arquivo}</span>
                      <span style={styles.meta}>
                        {a.atividade_id ? `Atividade #${a.atividade_id}` : 'Geral'} · {a.enviado_por} · {formatarData(a.criado_em)}
                      </span>
                    </div>
                    <div style={styles.acoes}>
                      <button style={styles.botaoBaixar} onClick={() => download(a)}>⬇ Baixar</button>
                      <button style={styles.botaoDeletar} onClick={() => deletar(a.id)}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { display: 'flex', height: '100vh', overflow: 'hidden', backgroundColor: '#f0f2f5' },
  conteudo: { flex: 1, padding: '2rem', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' },
  botaoVoltar: { backgroundColor: 'transparent', color: '#888', border: '1px solid #ddd', padding: '6px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' },
  titulo: { fontSize: '22px', color: '#1a1a2e', margin: 0 },
  subtitulo: { fontSize: '13px', color: '#888', margin: '2px 0 0' },
  selectTema: { padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' },
  botaoUpload: { backgroundColor: '#1a1a2e', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' },
  erro: { color: '#e53935', fontSize: '13px', marginBottom: '1rem' },
  vazio: { textAlign: 'center', padding: '4rem 0' },
  grupoTema: { backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0', marginBottom: '1rem', overflow: 'hidden' },
  temaHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' },
  temaTitulo: { fontSize: '14px', fontWeight: '600', color: '#1a1a2e' },
  temaCount: { fontSize: '12px', color: '#888' },
  lista: { display: 'flex', flexDirection: 'column' },
  item: { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderBottom: '1px solid #f5f5f5' },
  icone: { fontSize: '20px' },
  info: { flex: 1, display: 'flex', flexDirection: 'column' },
  nome: { fontSize: '13px', color: '#1a1a2e', fontWeight: '500' },
  meta: { fontSize: '11px', color: '#888', marginTop: '2px' },
  acoes: { display: 'flex', gap: '6px' },
  botaoBaixar: { backgroundColor: 'transparent', color: '#1976d2', border: '1px solid #1976d2', padding: '4px 12px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' },
  botaoDeletar: { backgroundColor: 'transparent', color: '#e53935', border: '1px solid #e53935', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' },
};